/**
 * Admin reads (Phase 6). Straight Supabase reads, same pattern as ../reviewer/queries.ts -
 * RLS grants is_admin() SELECT on these tables (see the Phase 6 migration), so only
 * mutations need a worker round-trip (see ../api.ts). Unlike the dashboard's reads, these
 * include inactive rows, since the admin screens edit the whole history, not just the
 * live grid.
 */
import { supabase } from '../supabase';
import type {
  AdminBankRow,
  AdminCheckSchemaRow,
  AdminRiskSchemaRow,
  AdminStandingInstructionRow,
  AgentConfigRow,
  AuditLogRow,
  CronSettingRow,
} from './types';

export async function fetchAllCheckSchema(): Promise<AdminCheckSchemaRow[]> {
  const { data, error } = await supabase
    .from('check_schema')
    .select('id, check_key, category, label, description, display_order, handler_type, bank_id, active')
    .order('display_order');
  if (error) throw error;
  return (data ?? []).map((c) => ({
    id: c.id,
    checkKey: c.check_key,
    category: c.category,
    label: c.label,
    description: c.description,
    displayOrder: c.display_order,
    handlerType: c.handler_type as 'static' | 'agentic',
    bankId: c.bank_id,
    active: c.active,
  }));
}

export async function fetchAllRiskSchema(): Promise<AdminRiskSchemaRow[]> {
  const { data, error } = await supabase
    .from('risk_matrix_schema')
    .select('id, risk_key, risk_group, label, description, display_order, comment_required_above, active')
    .order('display_order');
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    riskKey: r.risk_key,
    riskGroup: r.risk_group as 'property' | 'market',
    label: r.label,
    description: r.description,
    displayOrder: r.display_order,
    commentRequiredAbove: r.comment_required_above,
    active: r.active,
  }));
}

export async function fetchAllBanks(): Promise<AdminBankRow[]> {
  const { data, error } = await supabase.from('banks').select('id, name, aliases').order('name');
  if (error) throw error;
  return (data ?? []).map((b) => ({ id: b.id, name: b.name, aliases: b.aliases }));
}

export async function fetchAllStandingInstructions(): Promise<AdminStandingInstructionRow[]> {
  const { data, error } = await supabase
    .from('standing_instructions')
    .select('id, bank_id, is_institute_baseline, version, body, effective_from, active, created_at, storage_path, source_filename')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((s) => ({
    id: s.id,
    bankId: s.bank_id,
    isInstituteBaseline: s.is_institute_baseline,
    version: s.version,
    body: s.body,
    effectiveFrom: s.effective_from,
    active: s.active,
    createdAt: s.created_at,
    storagePath: s.storage_path,
    sourceFilename: s.source_filename,
  }));
}

export async function fetchAgentConfig(key: string): Promise<AgentConfigRow | null> {
  const { data, error } = await supabase.from('agent_config').select('*').eq('key', key).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    key: data.key,
    value: (data.value ?? {}) as Record<string, unknown>,
    updatedAt: data.updated_at,
    updatedBy: data.updated_by,
  };
}

export async function fetchCronSettings(): Promise<CronSettingRow[]> {
  const { data, error } = await supabase
    .from('cron_settings')
    .select('key, value, updated_at')
    .in('key', ['morning_summary_schedule', 'morning_summary_local_time_label']);
  if (error) throw error;
  return (data ?? []).map((c) => ({ key: c.key, value: c.value, updatedAt: c.updated_at }));
}

export async function fetchAuditLog(entities: string[], limit = 50): Promise<AuditLogRow[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('*')
    .in('entity', entities)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((a) => ({
    id: a.id,
    actor: a.actor,
    action: a.action,
    entity: a.entity,
    entityId: a.entity_id,
    detail: a.detail as AuditLogRow['detail'],
    createdAt: a.created_at,
  }));
}
